import React, { useEffect, useMemo, useState } from "react";
import { VisitQuestionnaireAnswer } from "@pages/ecoa/visit/VisitQuestionnaire";
import { CrfCheckItemSpec, CrfSingleSelectItemSpec, StudySpec } from "maven-lib/dist/consts/edc";
import { GetEcoaCapturesResponse } from "maven-lib/dist/dto/ecoaCapture";
import { EcoaVisitQuestionnaireData } from "maven-lib/dist/dto/ecoaVisit";
import { EcoaCrf, EcoaCrfStatus } from "maven-lib/dist/dto/entity/ecoaCrf";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { VisitQuestionnaireFooter } from "./VisitQuestionnaireFooter";
import { VisitQuestionnaireItem } from "./VisitQuestionnaireItem";
import { makeCaptureData } from "../../../lib";

interface VisitQuestionnaireInnerProps {
  studySpec: StudySpec;
  crfs: Array<EcoaCrf>;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
  questionnairesData: EcoaVisitQuestionnaireData;
  questionnaireSeq: number;
  questionnaireCount: number;
  questionnaire: EcoaQuestionnaire;
  captureResponse: GetEcoaCapturesResponse | null;
  questionnaireAnswersState: [Array<VisitQuestionnaireAnswer>, React.Dispatch<React.SetStateAction<VisitQuestionnaireAnswer[]>>];
  isAvailableItem: (questionnaire: EcoaQuestionnaire | null, answers: Array<VisitQuestionnaireAnswer>) => boolean;
}

export function VisitQuestionnaireInner({
  studySpec,
  crfs,
  folderId,
  visitId,
  visitSeq,
  crfPageId,
  crfPageSeq,
  questionnaireSeq,
  questionnaireCount,
  questionnaire,
  questionnairesData,
  captureResponse,
  questionnaireAnswersState,
  isAvailableItem,
}: VisitQuestionnaireInnerProps) {
  const capturedItems = useMemo(() => questionnaireAnswersState[0], [questionnaireAnswersState]);
  const [answers, setAnswers] = questionnaireAnswersState;

  useEffect(() => {
    setAnswers((preAnswers) => {
      if (preAnswers.length > 0) {
        return preAnswers;
      } else {
        const initialAnswers = (() => {
          if (questionnaire.item.original.type === "CHECK") {
            const checkItem: CrfCheckItemSpec = questionnaire.item.original;
            const items = checkItem.itemCode.codes.map(({ id: itemId, calcVal }) => ({ itemId, dbVal: null, calcVal: null }));
            const typeTextItems = checkItem.itemCode.codes
              .map(({ typeText }) => ({ itemId: typeText?.id || "", dbVal: null, calcVal: null }))
              .filter(({ itemId }) => itemId !== "");

            return [...items, ...typeTextItems];
          } else if (questionnaire.item.original.type === "SINGLE_SELECT") {
            const checkItem: CrfSingleSelectItemSpec = questionnaire.item.original;
            const typeTextItems = checkItem.itemCode.codes
              .map(({ typeText }) => ({ itemId: typeText?.id || "", dbVal: null, calcVal: null }))
              .filter(({ itemId }) => itemId !== "");

            return [{ itemId: questionnaire.item.id, dbVal: null, calcVal: null }, ...typeTextItems];
          } else if (questionnaire.item.original.type === "DATE") {
            const dateValues: Array<VisitQuestionnaireAnswer> = [{ itemId: questionnaire.item.id, dbVal: null, calcVal: null }];

            if (questionnaire.item.original.children?.items.length) {
              questionnaire.item.original.children?.items.forEach((childrenItem) => {
                if (childrenItem.type === "TIME") {
                  // 현재 TIME 아이템만 date questionnaire item 내부에서 구현 되어 있음
                  dateValues.push({ itemId: childrenItem.id, dbVal: null, calcVal: null });
                }
              });
            }

            return [...dateValues];
          } else {
            return [{ itemId: questionnaire.item.id, dbVal: null, calcVal: null }];
          }
        })();

        return initialAnswers;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire]);

  const [disabledFooter, setDisabledFooter] = useState<boolean>(false);

  const crf = crfs.find(
    (c) =>
      c.folderId === folderId &&
      c.visitId === visitId &&
      c.visitSeq === visitSeq &&
      c.crfPageId === crfPageId &&
      c.crfPageSeq === crfPageSeq
  );

  const getCaptureParams = (): Array<StudyCaptureData | null> | null => {
    const typeTextItemIds: Array<string> = [];

    for (const answer of answers) {
      if (questionnaire.item.original.type === "SINGLE_SELECT") {
        questionnaire.item.original.itemCode.codes.forEach((val) => {
          if (answer?.dbVal === val?.dbVal && val?.typeText?.id) {
            typeTextItemIds.push(val.typeText.id);
          }
        });
      } else if (questionnaire.item.original.type === "CHECK") {
        questionnaire.item.original.itemCode.codes.forEach((val) => {
          if (answer?.itemId === val.id && val?.typeText?.id && (answer.dbVal || typeof answer.dbVal === "number")) {
            typeTextItemIds.push(val.typeText.id);
          }
        });
      }
    }

    const payloads: Array<StudyCaptureData | null> = capturedItems
      .map<StudyCaptureData | null>((capturedItem) => {
        const answer = answers.find((ans) => ans.itemId === capturedItem.itemId) || { itemId: null, dbVal: null };
        if (!answer.itemId) return null;

        if (typeTextItemIds?.some((typeTextItemId) => typeTextItemId === answer.itemId) && (answer.dbVal === null || answer.dbVal === "")) {
          return null;
        } else if (answer?.dbVal || typeof answer?.dbVal === "number") {
          // valid answer dbVal
          return makeCaptureData(questionnaire.item, answer, capturedItem.dbVal, folderId, visitId, visitSeq, crfPageId, crfPageSeq);
        } else {
          // invalid answer dbVal
          if (capturedItem?.dbVal || typeof capturedItem?.dbVal === "number") {
            // update dbVal to null
            return makeCaptureData(questionnaire.item, answer, null, folderId, visitId, visitSeq, crfPageId, crfPageSeq);
          }
        }

        return null;
      })
      .filter((e) => e);

    if (!payloads.length) return null;
    else if (payloads.every((e) => e?.val.dbVal === null || e?.val.dbVal === "")) return null;
    else if (typeTextItemIds.length && !typeTextItemIds.every((id) => payloads.find((e) => e?.id.itemId === id))) return null;
    return payloads;
  };

  const handleDisabledFooterChange = (val: boolean) => {
    setDisabledFooter(val);
  };

  const handleValueChange = (itemValues: Array<VisitQuestionnaireAnswer>): void => {
    setAnswers(itemValues);
  };

  // 현재는 선택된 값이 없으면 post body params에 null이 전달되면서 캡쳐가 진행되지 않도록 처리되어있기 때문에 아래 코드가 크게 효능이 없는 상태라서 주석 처리해놓음
  // const getIsDisabledFooterButtonByAnswer = () => {
  //   if (questionnaire.item.original.type === "CHECK") {
  //     if (questionnaire.item.original.itemCode.codes.length > 1) {
  //       return !questionnaire.item.original.itemCode.codes.some((code) => {
  //         const foundItemDbVal = answers.find((answer) => answer.itemId === code.id)?.dbVal;
  //         return !(foundItemDbVal || typeof foundItemDbVal === "number"); // 입력값 0에 대한 대응
  //       });
  //     } else {
  //       return false;
  //     }
  //   } else if (questionnaire.item.original.type === "DATE") {
  //     const isValidDateDbVal = !!answers.find((answer) => answer?.itemId === questionnaire.item.id)?.dbVal;
  //     const isValidChild: boolean =
  //       !questionnaire.item.original.children?.items?.length ||
  //       questionnaire.item.original.children.items.every(
  //         (item) => answers.find((answer) => item.type === "TIME" && answer.itemId === item.id)?.dbVal
  //       );

  //     return !(isValidDateDbVal && isValidChild);
  //   } else {
  //     const foundItemDbVal = answers.find((answer) => answer?.itemId === questionnaire.item.id)?.dbVal;
  //     return !(foundItemDbVal || typeof foundItemDbVal === "number"); // 입력값 0에 대한 대응
  //   }
  // };

  const isLastQuestionnaire = questionnaireSeq === questionnaireCount;

  const available = useMemo(() => isAvailableItem(questionnaire, answers), [isAvailableItem, questionnaire, answers]);

  const getFooterButtonType = (() => {
    if (questionnaire.item.type === "CONSTANT") return "NO_CAPTURE"; // TODO: CONSTANT에 대한 처리가 추가되면 삭제 예정

    if ([EcoaCrfStatus.Ongoing, EcoaCrfStatus.NotDone].includes(crf?.status!)) {
      if (!captureResponse) {
        return "EMPTY";
      } else {
        if (available || isLastQuestionnaire) {
          return "CAPTURE";
        } else {
          return "NO_CAPTURE";
        }
      }
    }
    return "NO_CAPTURE";
  })();

  return (
    <>
      <VisitQuestionnaireItem
        studySpec={studySpec}
        folderId={folderId}
        visitId={visitId}
        visitSeq={visitSeq}
        crfPageId={crfPageId}
        crfPageSeq={crfPageSeq}
        questionnaire={questionnaire}
        defaultItemValues={answers}
        capturedItems={capturedItems}
        disabled={!captureResponse || crf?.status === EcoaCrfStatus.Done || !available}
        isAvailable={available}
        onValueChange={handleValueChange}
        onChangeDisabledFooter={handleDisabledFooterChange}
        captureResponse={captureResponse}
      />
      <VisitQuestionnaireFooter
        buttonType={getFooterButtonType}
        disabled={disabledFooter} // (available && getIsDisabledFooterButtonByAnswer())
        isLastQuestionnaire={isLastQuestionnaire}
        progressBarValue={((questionnaireSeq - 1) / questionnaireCount) * 100.0}
        crfStatus={crf!.status}
        folderId={folderId}
        visitId={visitId}
        visitSeq={visitSeq}
        crfPageId={crfPageId}
        crfPageSeq={crfPageSeq}
        questionnaire={questionnaire}
        getCaptureData={getCaptureParams}
        questionnairesData={questionnairesData}
        questionnaireSeq={questionnaireSeq}
        isShowPopup={questionnaire.item.original.ecoa?.popup}
        submitConfirmPopup={studySpec.config?.ecoa?.submitConfirmPopup}
      />
    </>
  );
}
